/**
 * Every release this system has deployed, newest first.
 *
 * The home page is deliberately modal and shows only the release in front of you, so
 * this is where the other questions get answered: when did we last ship, what went out
 * in v1.1259, was anything skipped. Ordered by build number rather than by any
 * timestamp, because the version *is* the sequence and a re-run host can write an
 * earlier release's document later than a newer one's.
 */
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert,
    Chip,
    Link,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useDefaultReleases, useNow, CAPTION_TICK_MS } from '../hooks';
import { compareVersion } from '../lib/homeMode';
import { agoTextCoarse } from '../lib/hostStatus';
import { changedComponents } from '../lib/releaseSummary';

export function ReleasesPage() {
    const { value: releases, loading } = useDefaultReleases();
    const now = useNow(CAPTION_TICK_MS);
    const ordered = [...releases].sort((a, b) => compareVersion(b.version, a.version));

    if (loading) {
        return <Typography color="text.secondary">Loading…</Typography>;
    }

    return (
        <Stack spacing={2}>
            <Stack spacing={0.5}>
                <Typography variant="h5" component="h1">
                    Releases
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '80ch' }}>
                    Every release the deploy half has projected. A release opened before this system took over the
                    deploy has no row here; its tag is still the record.
                </Typography>
            </Stack>

            {ordered.length === 0 ? (
                <Alert severity="info">No release has been deployed through this system yet.</Alert>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Version</TableCell>
                            <TableCell>Opened</TableCell>
                            <TableCell>Closed</TableCell>
                            <TableCell>Components changed</TableCell>
                            <TableCell>Announced</TableCell>
                            <TableCell>State</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {ordered.map((release) => {
                            const opened = release.openedAt?.toMillis?.();
                            const closed = release.closedAt?.toMillis?.();
                            return (
                                <TableRow key={release.id} hover>
                                    <TableCell>
                                        <Link component={RouterLink} to={`/releases/${release.version}`}>
                                            {release.version}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        {opened === undefined ? '—' : agoTextCoarse(Math.max(0, now - opened))}
                                    </TableCell>
                                    <TableCell>
                                        {closed === undefined ? '—' : agoTextCoarse(Math.max(0, now - closed))}
                                    </TableCell>
                                    <TableCell>{changedComponents(release.plan).length}</TableCell>
                                    <TableCell>{release.announcement?.postedAt ? 'yes' : 'no'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            color={release.phase === 'closed' ? 'default' : 'primary'}
                                            label={release.phase}
                                        />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
        </Stack>
    );
}
